import React, { useState } from "react";
import SEOHead from "./SEOHead";
import { contactPageSchema } from "../data/seoSchemas";
import { api } from "../services/api";
import "./Contact.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    setErrorMessage("");

    try {
      await api.createContact(formData);
      setStatus("success");
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => setStatus(null), 6000);
    } catch (error) {
      setStatus("error");
      setErrorMessage(error.message || "Failed to deliver message. Please call our clinic directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="contact-section section-padding">
      <SEOHead
        title="Contact Dr. Sunil Apsingi | Clinic Location & Phone | Medicover HITEC City"
        description="Get in touch with Dr. Sunil Apsingi's clinic at Medicover Out Patient Center, HITEC City, Hyderabad. Phone: +91-8341555765. In-person OPD timings, appointments, and inquiry form."
        canonicalPath="/contact"
        schemaJson={contactPageSchema}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]}
      />
      <div className="container">
        <div className="text-center mb-5">
          <span className="section-header-pill">
            <i className="fa-solid fa-headset me-1"></i> Patient Desk
          </span>
          <h1 className="section-title-large">Get in Touch</h1>
          <p className="section-subtitle-muted">
            Have an inquiry about an upcoming surgery, medical report review, or appointment? Send us a message or contact our outpatient desk directly.
          </p>
        </div>

        <div className="contact-wrapper shadow-lg rounded-4 overflow-hidden">
          <div className="row g-0">
            {/* Left Column: Direct Contact Details */}
            <div className="col-lg-5 contact-info-col p-4 p-lg-5">
              <div className="info-content">
                <h3 className="text-white mb-2">Hospital Desk</h3>
                <p className="text-white-50 mb-4">
                  Dr. Sunil Apsingi conducts in-person outpatient consultations at Medicover Hospital, HITEC City.
                </p>

                <div className="info-item-modern d-flex gap-3 mb-4">
                  <div className="icon-circle-modern">
                    <i className="fa-solid fa-phone"></i>
                  </div>
                  <div>
                    <span className="info-label text-white-50 small d-block">Phone Support</span>
                    <a href="tel:+918341555765" className="text-white text-decoration-none fw-bold">
                      +91 8341555765
                    </a>
                    <span className="d-block text-white-50 small">040-68334455</span>
                  </div>
                </div>

                <div className="info-item-modern d-flex gap-3 mb-4">
                  <div className="icon-circle-modern">
                    <i className="fa-solid fa-envelope"></i>
                  </div>
                  <div>
                    <span className="info-label text-white-50 small d-block">Official Email</span>
                    <a href="mailto:contact@arthroscopydoc.com" className="text-white text-decoration-none fw-bold">
                      contact@arthroscopydoc.com
                    </a>
                  </div>
                </div>

                <div className="info-item-modern d-flex gap-3 mb-4">
                  <div className="icon-circle-modern">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>
                  <div>
                    <span className="info-label text-white-50 small d-block">Outpatient Location</span>
                    <p className="text-white mb-0 small">
                      Medicover Out Patient Center, HUDA Techno Enclave, HITEC City, Hyderabad, Telangana 500081
                    </p>
                  </div>
                </div>

                <div className="info-item-modern d-flex gap-3">
                  <div className="icon-circle-modern">
                    <i className="fa-regular fa-clock"></i>
                  </div>
                  <div>
                    <span className="info-label text-white-50 small d-block">Consultation Hours</span>
                    <p className="text-white mb-0 small">
                      Monday – Saturday: 10:00 AM – 04:00 PM
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Interactive Form */}
            <div className="col-lg-7 contact-form-col bg-white p-4 p-lg-5">
              <h3 className="form-heading text-navy mb-4">Send a Message</h3>

              {status === "success" && (
                <div className="alert alert-success d-flex align-items-center gap-2 mb-4 py-3">
                  <i className="fa-solid fa-circle-check fs-5"></i>
                  <span>Your message has been delivered to Dr. Sunil's team. We will contact you shortly.</span>
                </div>
              )}

              {status === "error" && (
                <div className="alert alert-danger d-flex align-items-center gap-2 mb-4 py-3">
                  <i className="fa-solid fa-circle-exclamation fs-5"></i>
                  <span>{errorMessage}</span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label htmlFor="contactName" className="medical-label">Your Name *</label>
                    <input
                      type="text"
                      id="contactName"
                      className="medical-input"
                      placeholder="e.g. Anand Varma"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="contactEmail" className="medical-label">Email Address *</label>
                    <input
                      type="email"
                      id="contactEmail"
                      className="medical-input"
                      placeholder="e.g. anand@gmail.com"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contactMessage" className="medical-label">Inquiry / Message *</label>
                  <textarea
                    id="contactMessage"
                    className="medical-input"
                    placeholder="Briefly describe your query or case details..."
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                <button
                  className="btn-medical-primary w-100 py-3"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin me-2"></i> Sending Message...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane me-2"></i> Send Inquiry
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;